import { apiUrl } from "../../api/endpoints";
import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import DataGridTable from "../../Components/DataGridTable";
import Spinner from "../../Components/Spinner";

import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from 'react-router-dom';
import Alert from '../../Components/Alert';

import { LanguageContext } from "../../context/LanguageContext";
import AddIcon from "@mui/icons-material/Add";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import "../../Components/modal.css";
import "./UserHealthDetails.css";
import {
  getHealthTranslations,
  HealthPageHeader,
  PermissionDenied,
  isRtlLanguage,
} from "./userHealthShared";
export const MyHealth = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activeScan, setActiveScan] = useState(null);
  const [healthProfile, setHealthProfile] = useState({
    customerId: "",
    name: "",
    sex: "M",
    age: "",
    height: "",
    phone: "",
  });
  const navigate = useNavigate();

  const { permissions } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = getHealthTranslations(lang);

  const canView = permissions["view_my_health_data"] || permissions["view_users_health"];
  const [openConfirm, setOpenConfirm] = useState(false);
  const [shareWithAdmin, setShareWithAdmin] = useState(false);
  const isRtl = isRtlLanguage(lang);
  const { user } = useAuth();
  const pagingoptions = [10, 20, 50, 100];


  const columns = [
    {
      field: "userName",
      headerName: t.columns.user,
      width: 250,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "weight",
      headerName: t.columns.weight,
      width: 150,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "height",
      headerName: t.columns.height,
      width: 150,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "bmi",
      headerName: t.columns.bmi,
      width: 120,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "age",
      headerName: t.columns.age,
      width: 120,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "gmtCreate",
      headerName: t.columns.createdAt,
      width: 200,
      align: "center",
      headerAlign: "center"


    },
  ];
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = (type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? 'left' : 'right'
    });
  };
  const fetchData = useCallback(async () => {
    if (!canView) return;

    setLoading(true);

    try {
      const sort = sortModel?.[0] || {};
      const res = await axios.get(apiUrl(`user-health/my`), {
        params: {
          page,
          pageSize,
          sortField: sort.field || "id",
          sortOrder: sort.sort || "desc",
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });

      if (Array.isArray(res.data?.data)) {
        const formatted = res.data.data.map((item) => ({
          ...item,
          userName: item.user?.name || "",
        }));

        setRows(formatted);
        setRowCount(Number(res.data?.pagination?.total ?? formatted.length));
      } else {
        setRows([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("UserHealth fetch error:", err);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, filterModel, page, pageSize, sortModel]);

  const fetchScanState = useCallback(async () => {
    if (!canView) return;

    try {
      const res = await axios.get(apiUrl("medical-device/control-data"));
      const data = res.data?.DATA || {};
      const currentUser = (data.users || []).find((item) => Number(item.id) === Number(user?.id));
      const scan = (data.devices || []).find(
        (item) =>
          Number(item.user_id) === Number(user?.id) &&
          String(item.scanStatus || "").toLowerCase() === "scanning"
      );

      setActiveScan(scan || null);
      setHealthProfile((prev) => ({
        ...prev,
        customerId: currentUser?.healthCustomerId || currentUser?.phone || user?.phone || prev.customerId,
        name: currentUser?.healthName || currentUser?.name || user?.name || prev.name,
        sex: currentUser?.healthSex || prev.sex || "M",
        age: currentUser?.healthAge || prev.age,
        height: currentUser?.healthHeight || prev.height,
        phone: currentUser?.healthPhone || currentUser?.phone || user?.phone || prev.phone,
      }));
    } catch (err) {
      console.error("UserHealth scan state error:", err);
    }
  }, [canView, user?.id, user?.name, user?.phone]);
  const handleRowClick = (params) => {
    navigate(`/my-health/details/${params.row.id}`, {
      state: { from: "/my-health", source: "my-health" },
    });
  };

  useEffect(() => {
    fetchData();
    fetchScanState();
  }, [fetchData, fetchScanState]);

  const handleProfileChange = (field, value) => {
    setHealthProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleStartScan = async () => {
    setOpenConfirm(false); 

    try {
      setStarting(true);

      const res = await axios.post(apiUrl(`medical-device/start-scan`), {
        userId: user?.id,
        isShared: shareWithAdmin,
        ...healthProfile,
      });

      showAlert(
        'success',
        res.data?.INFO || t.actions.startScanTitle
      );

      fetchData();
      fetchScanState();

    } catch (err) {
      const message =
        err?.response?.data?.INFO ||
        err?.response?.data?.message ||
        err.message ||
        t.actions.startScanTitle;

      showAlert('error', message);
    } finally {
      setStarting(false);
    }
  };

  const handleCancelScan = async () => {
    if (!activeScan?.id) return;

    try {
      setCancelling(true);
      const res = await axios.post(apiUrl("medical-device/cancel-scan"), {
        healthDeviceId: activeScan.id,
      });
      showAlert("success", res.data?.INFO || (t.actions.scanCancelled || "Scan cancelled"));
      await fetchScanState();
    } catch (err) {
      const message =
        err?.response?.data?.INFO ||
        err?.response?.data?.message ||
        err.message ||
        (t.actions.scanCancelFailed || "Failed to cancel scan");
      showAlert("error", message);
    } finally {
      setCancelling(false);
    }
  };
  if (!canView) {
    return (
      <PermissionDenied title={t.states.permissionDenied} message={t.states.cannotViewHealth} />
    );
  }

  return (
    <div className="configuration devices-page health-page" dir={isRtl ? "rtl" : "ltr"} style={{ height: "100%" }}>
      <div className="device-main health-page-shell">
        <HealthPageHeader
          title={t.pages.myHealth}
          subtitle={t.descriptions.myHealth}
          actions={
          activeScan ? (
            <button
              className="devices-primary-btn health-danger-btn"
              onClick={handleCancelScan}
              disabled={cancelling}
            >
              <CancelOutlinedIcon style={{ marginInlineEnd: "8px" }} />
              <span>{cancelling ? (t.actions.cancelling || "Cancelling...") : (t.actions.cancelScan || "Cancel Scan")}</span>
            </button>
          ) : (
            <button
              className="devices-primary-btn"
              onClick={() => {
                setShareWithAdmin(false);
                setOpenConfirm(true);
              }}
            >
              <AddIcon style={{ marginInlineEnd: "8px" }} />
              <span>{t.actions.startScan}</span>
            </button>
          )
          }
        />

        {openConfirm ? (
          <div id="modal" className="health-scan-modal-overlay" onMouseDown={() => setOpenConfirm(false)}>
            <div
              id="popup"
              className="dashboard-modal health-scan-modal"
              onMouseDown={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="dashboard-header health-scan-modal__header">
                <div className="dashboard-header-text">
                  <h2>{t.actions.startScanTitle}</h2>
                </div>
              </div>
              <div className="modal-divider" />
              <div className="modal-content dashboard-content health-scan-modal__content">
                <p className="health-scan-modal__message">{t.actions.startScanConfirm}</p>
                <div className="health-profile-grid">
                  <label>
                    <span>{t.labels.user || "Name"}</span>
                    <input value={healthProfile.name} onChange={(event) => handleProfileChange("name", event.target.value)} />
                  </label>
                  <label>
                    <span>{t.labels.phone || "Phone"}</span>
                    <input value={healthProfile.phone} onChange={(event) => handleProfileChange("phone", event.target.value)} />
                  </label>
                  <label>
                    <span>{t.labels.sex}</span>
                    <select value={healthProfile.sex} onChange={(event) => handleProfileChange("sex", event.target.value)}>
                      <option value="M">M</option>
                      <option value="F">F</option>
                    </select>
                  </label>
                  <label>
                    <span>{t.labels.age}</span>
                    <input type="number" min="1" value={healthProfile.age} onChange={(event) => handleProfileChange("age", event.target.value)} />
                  </label>
                  <label>
                    <span>{t.labels.height}</span>
                    <input type="number" min="1" step="0.1" value={healthProfile.height} onChange={(event) => handleProfileChange("height", event.target.value)} />
                  </label>
                  <label>
                    <span>Customer ID</span>
                    <input value={healthProfile.customerId} onChange={(event) => handleProfileChange("customerId", event.target.value)} />
                  </label>
                </div>
                <label className="health-scan-share-option">
                  <input
                    type="checkbox"
                    checked={shareWithAdmin}
                    onChange={(event) => setShareWithAdmin(event.target.checked)}
                  />
                  <span>
                    <strong>{t.actions.shareScanWithAdmin || "Share scan data with admin"}</strong>
                    <small>{t.actions.shareScanWithAdminHint || "Admins can view this scan only when this option is checked."}</small>
                  </span>
                </label>
              </div>
              <div className="modal-divider" />
              <div className="modal-footer dashboard-footer health-scan-modal__footer">
                <div className="modal-footer-right-buttons">
                  <button
                    type="button"
                    className="btn btn-cancel-details"
                    onClick={() => setOpenConfirm(false)}
                  >
                    {t.actions.cancel}
                  </button>
                  <button
                    type="button"
                    className="btn btn-apply"
                    onClick={handleStartScan}
                    disabled={starting}
                  >
                    {starting ? t.actions.starting : t.actions.confirmStart}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />
        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className="table-wrapper" style={{ flex: 1 }}>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"id"}
              pagingoptions={pagingoptions}
              sortField="created_at"
              sortType="desc"
              showToolbar={true}
              tableKey="user_health"
              loading={loading}
              handleRowClick={handleRowClick}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: setPage,
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}

            />
          </div>
        ) : (
          <div className="health-empty-state">{t.states.noData}</div>
        )}
      </div>
    </div>
  );
};
